import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Edit, HelpCircle, CheckCircle2 } from "lucide-react";

function TriviaPanel() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const today = new Date().toISOString().slice(0,10);
  const blank = { question: "", options: ["", "", "", ""], correct_index: 0, category: "", active_date: today };

  const refresh = () => api.get("/admin/trivia").then((r) => setList(r.data));
  useEffect(() => { refresh(); }, []);

  const save = async () => {
    const cleaned = { ...editing, options: editing.options.map((o) => o.trim()).filter(Boolean) };
    if (cleaned.options.length < 2) { toast.error("At least 2 options needed"); return; }
    if (cleaned.correct_index >= cleaned.options.length) { toast.error("Correct option index is out of range"); return; }
    if (!cleaned.question.trim()) { toast.error("Question is required"); return; }
    if (!cleaned.active_date) { toast.error("Active date is required"); return; }
    try {
      if (cleaned.id) {
        await api.put(`/admin/trivia/${cleaned.id}`, cleaned);
        toast.success("Question updated");
      } else {
        await api.post("/admin/trivia", cleaned);
        toast.success("Question created");
      }
      setEditing(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (tid) => {
    if (!confirm("Delete this question?")) return;
    await api.delete(`/admin/trivia/${tid}`);
    toast.success("Deleted");
    refresh();
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="font-black uppercase flex items-center gap-2"><HelpCircle size={18}/> Trivia Questions</h3>
        <button data-testid="trivia-new-btn" onClick={() => setEditing(blank)} className="brutal-btn inline-flex items-center gap-1">
          <Plus size={14}/> New Question
        </button>
      </div>

      {list.length === 0 ? (
        <p className="text-sm text-neutral-600">{`No trivia questions yet. Click "New Question" to create one.`}</p>
      ) : (
        <div className="grid gap-2">
          {list.map((q) => {
            const isToday = q.active_date === today;
            return (
              <div key={q.id} data-testid={`trivia-row-${q.id}`} className="border-2 border-black p-3 flex items-start gap-3" style={{background: isToday ? "#FFFCE5" : "white"}}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-xs uppercase font-mono-bq mb-1">
                    <span className="border border-black px-1.5 py-0.5">{q.active_date}</span>
                    {q.category && <span className="border border-black px-1.5 py-0.5">{q.category}</span>}
                    {isToday && <span className="border border-black px-1.5 py-0.5 bg-black text-white">LIVE TODAY</span>}
                  </div>
                  <div className="font-black truncate">{q.question}</div>
                  <div className="text-xs text-neutral-600 mt-1">
                    {q.options.map((opt, i) => (
                      <span key={`${q.id}-opt-${i}`} className={`inline-flex items-center gap-1 mr-3 ${i === q.correct_index ? "font-black text-black" : ""}`}>
                        {i === q.correct_index && <CheckCircle2 size={10}/>}
                        {String.fromCharCode(65 + i)}. {opt}
                      </span>
                    ))}
                  </div>
                </div>
                <button onClick={() => setEditing(q)} data-testid={`trivia-edit-${q.id}`} className="brutal-btn text-xs"><Edit size={12}/></button>
                <button onClick={() => remove(q.id)} data-testid={`trivia-del-${q.id}`} className="brutal-btn text-xs"><Trash2 size={12}/></button>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="border-4 border-black rounded-none max-w-xl">
          <DialogHeader><DialogTitle className="uppercase font-black">{editing?.id ? "Edit" : "New"} Question</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <label className="block text-xs uppercase font-black">Active date</label>
              <input data-testid="trivia-form-date" type="date" value={editing.active_date} onChange={(e) => setEditing({ ...editing, active_date: e.target.value })} className="border-2 border-black p-2 w-full"/>
              <label className="block text-xs uppercase font-black">Question</label>
              <textarea data-testid="trivia-form-question" value={editing.question} onChange={(e) => setEditing({ ...editing, question: e.target.value })} className="border-2 border-black p-2 w-full" rows={2}/>
              <label className="block text-xs uppercase font-black">Category (optional)</label>
              <input data-testid="trivia-form-category" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="border-2 border-black p-2 w-full"/>
              <label className="block text-xs uppercase font-black">Options (pick the correct one)</label>
              {editing.options.map((opt, i) => (
                <div key={`edit-opt-${i}`} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="trivia-correct"
                    data-testid={`trivia-form-correct-${i}`}
                    checked={editing.correct_index === i}
                    onChange={() => setEditing({ ...editing, correct_index: i })}
                    className="w-5 h-5"
                  />
                  <span className="font-black w-6">{String.fromCharCode(65 + i)}.</span>
                  <input
                    data-testid={`trivia-form-option-${i}`}
                    value={opt}
                    onChange={(e) => {
                      const next = [...editing.options];
                      next[i] = e.target.value;
                      setEditing({ ...editing, options: next });
                    }}
                    className="border-2 border-black p-2 flex-1"
                  />
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <button data-testid="trivia-form-save" onClick={save} className="brutal-btn">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default TriviaPanel;
